/**
 * spec-008 [T080] — o PORTEIRO do microfone: conversa de sala não vira turno.
 *
 * ## O defeito, medido
 *
 * G5, 19/09/2026, uma sessão real de 5 minutos: 30 janelas de 10 s com
 * conversa de fundo produziram **35 respostas espontâneas** contra um teto de
 * 2. A IA respondeu mais vezes do que houve janelas — toda frase ao redor
 * virou turno, e algumas geraram mais de uma resposta.
 *
 * ★ E o número **subiu** quando o fundo foi baixado para um DÉCIMO do volume
 * da fala direta (31 → 35). O gatilho do provedor não distingue por energia.
 *
 * ## Por que o conserto tem de ser aqui
 *
 * Não há onde configurar isso do lado de lá. Medido na [T068], contra o
 * provedor: `session.turn_detection`, `session.audio.turn_detection`,
 * `session.audio.input`, `session.vad` e `session.interruptions` respondem
 * todos `Unknown parameter`, e `session.audio` só aceita `output` e `format`.
 * **Se o áudio chegar lá, ele vira turno.** Então o único lugar onde a sala
 * pode ser barrada é antes de sair do navegador.
 *
 * O mecanismo já estava provado no transporte (`alternarMudo`): `enabled =
 * false` numa trilha local faz o navegador **não enviar** — não é evento de
 * protocolo, não depende do canal estar vivo, e o provedor não recebe nada
 * para interpretar como fala. O que faltava era o gatilho automático.
 *
 * ## ⚠️ O risco desta peça, dito antes do código
 *
 * Um portão fechado demais é **pior que o defeito que ele conserta**: falso
 * disparo custa um turno; portão travado custa a VOZ INTEIRA, e o consultor
 * não tem como saber por quê. Três decisões saem daí:
 *
 * 1. **Abre no PRIMEIRO quadro acima do limiar** — sem histerese na subida.
 *    Exigir fala sustentada para abrir cortaria a primeira sílaba, e um "não"
 *    virando "ão" é defeito pior que a sala entrando.
 * 2. **Fecha só depois de `MS_DE_SEGURAR_ABERTO` em silêncio** — pausa no meio
 *    da frase não fecha a porta.
 * 3. **Falha ABERTA.** Sem `AudioContext`, com stream inválido ou com qualquer
 *    erro do medidor, a trilha fica habilitada e a sessão segue sem porteiro —
 *    exatamente como hoje. É a mesma escolha do barge-in, pelo mesmo motivo:
 *    acessório não derruba o principal.
 *
 * ## O que ele NÃO faz
 *
 * - **Não desfaz o mudo do consultor.** Quem clicou em mudo mandou calar; o
 *   porteiro só decide entre "deixa passar" e "segura" DENTRO do estado não
 *   mudo. Por isso `estaMudo` é injetado em vez de lido de um global.
 * - **Não fala com o provedor.** Nenhum evento, nenhum `session.update`.
 * - **Não decide barge-in.** São dois juízes com limiares diferentes sobre o
 *   mesmo sinal, e desde a [T095] o porteiro abre MAIS ALTO (0,15) do que o
 *   barge-in interrompe (0,04).
 *
 *   ⚠️ **Esta linha dizia o contrário até a [T100]** — *"o porteiro abre no
 *   limiar mais baixo; abrir ANTES é o que garante que a interrupção chegue ao
 *   provedor"* — e continuou dizendo depois que o conserto do G5 inverteu a
 *   relação. Nenhum teste reprovou, porque comentário não é invariante. O que
 *   restaurou a garantia foi `abrirAgora()`: quando o barge-in dispara, a
 *   porta OBEDECE em vez de re-julgar. Custo de descobrir: G4 com **calou 5/5
 *   e retomou 0/5** — a IA calando e ficando surda.
 */
import { criarMedidorDeNivel } from './captura-de-voz'

/**
 * Quanto tempo a porta fica aberta depois que a fala some.
 *
 * 800 ms é mais longo que a pausa entre duas frases do mesmo pedido e mais
 * curto que o intervalo em que a sala volta a ser só sala. Fechar rápido
 * demais picotaria o pedido do consultor em vários turnos — que é o mesmo
 * defeito do G5, só que com a voz certa.
 */
export const MS_DE_SEGURAR_ABERTO = 800

/**
 * ★★★ [spec-008 T095] O limiar de ABERTURA — MEDIDO, e não mais herdado.
 *
 * ⚠️ **A primeira versão reusava `LIMIAR_VAD_RMS` (0,02)** com o argumento de
 * que "a pergunta é a mesma". **Ela não é**: aquele limiar responde *"há fala
 * aqui?"*; este responde *"é o consultor, perto do microfone?"*. Reusar o
 * número fez o porteiro abrir para a sala inteira — medido no G5 com ele
 * ligado: **44 disparos**, pior que os 35 sem ele.
 *
 * ★ Os números que decidem, medidos em 19/09/2026 na própria sonda do G5
 * (fundo a 0,25 de ganho, fala direta a 2,5):
 *
 * | | RMS |
 * |---|---:|
 * | fundo, mediana | **0,0510** |
 * | fundo, **pior caso** | **0,1155** |
 * | fala direta | **0,3282** |
 * | ~~limiar anterior~~ | ~~0,0200~~ — abaixo até da MEDIANA do fundo |
 *
 * O pior fundo é **35,2%** da fala direta, então existe uma janela larga
 * (0,1155 → 0,3282) em que um limiar separa os dois.
 *
 * **0,15** fica acima de todo o fundo medido e deixa **2,2× de folga** para a
 * fala direta. A folga é deliberada e assimétrica: barrar o consultor é pior
 * que deixar a sala entrar.
 *
 * ⚠️ **O que este número assume, declarado:** o fundo é sintético (o mesmo TTS
 * dos outros estímulos) e entra pelo canal do microfone. Sala real tem
 * reverberação e sobreposição, e a razão de 35% pode piorar. Se piorar a ponto
 * de encostar em 0,15, o caminho **não** é baixar o limiar — é trocar de
 * abordagem (reconhecimento de locutor, supressão no provedor, push-to-talk),
 * porque aí não existe mais janela.
 */
export const LIMIAR_DO_PORTEIRO_RMS = 0.15

/**
 * ★★★ [spec-008 T100] Quanto tempo a porta fica aberta DEPOIS de um barge-in.
 *
 * Não é margem de segurança inventada: a interrupção medida no G4 de
 * 19/09/2026 durou **1.907 ms**, e o fechamento normal são 800 ms de silêncio.
 * Sem uma janela garantida, a porta que o barge-in abriu fecharia **no meio da
 * frase do consultor** — ele diria a segunda metade para um microfone surdo,
 * que é uma forma pior do mesmo defeito.
 *
 * 2.500 ms cobre a frase medida com folga e devolve a decisão ao nível logo
 * em seguida. Quem encerra o turno de verdade é a detecção do provedor, no
 * silêncio — esta janela só garante que ele receba a frase inteira.
 */
export const MS_ABERTO_APOS_BARGE_IN = 2500

/**
 * ★ [spec-008 T084] Quanto tempo sem MEDIÇÃO NENHUMA faz o porteiro desistir e
 * abrir de vez.
 *
 * ⚠️ **Existe por um defeito medido no próprio porteiro, no dia em que ele
 * nasceu.** `criarMedidorDeNivel` mede uma vez de forma SÍNCRONA e depois
 * depende de `requestAnimationFrame`. Quando o rAF é estrangulado — aba em
 * segundo plano, navegador headless, economia de energia — a única medição que
 * chega é a inicial, que pega silêncio. O portão então fecha aos
 * `MS_DE_SEGURAR_ABERTO` e **nunca mais reabre**, porque reabrir depende de uma
 * medição que não vem.
 *
 * ★ É exatamente o modo de falha que o cabeçalho deste arquivo diz evitar:
 * *"portão travado custa a VOZ INTEIRA, e o consultor não tem como saber por
 * quê"*. A decisão 3 (falha aberta) cobria o medidor que **lança**; não cobria
 * o medidor que **emudece**.
 *
 * Medido em 19/09/2026: três sessões pagas seguidas abriram, a IA cumprimentou,
 * e **nenhuma run foi criada** — o pedido falado nunca chegou ao provedor.
 *
 * 2.000 ms é muito acima do intervalo de um rAF saudável (~16 ms, e ~1.000 ms
 * mesmo estrangulado) e bem abaixo de qualquer paciência humana.
 */
export const MS_SEM_MEDICAO_ABRE_DE_VEZ = 2000

/**
 * @param {Object} params
 * @param {MediaStream} params.stream O microfone JÁ aberto pelo transporte.
 * @param {Function} params.estaMudo Diz se o consultor mutou na mão.
 * @param {Function=} params.aoMudar Observador `(passando: boolean)` — só
 *   diagnóstico e medição; não decide nada.
 * @returns {{iniciar: Function, parar: Function, passando: Function}}
 */
/**
 * ★★ [spec-008 T085] O que o medidor OUVE — uma CÓPIA, nunca a trilha que o
 * porteiro fecha.
 *
 * ⚠️ **O defeito que isto conserta é um laço que trava, e ele derrubou três
 * sessões pagas seguidas.** `track.enabled = false` faz a trilha produzir
 * SILÊNCIO para todos os consumidores — inclusive para um
 * `MediaStreamAudioSourceNode` ligado a ela. Medindo a saída da própria trilha
 * que fecha, o porteiro fica **cego no instante em que fecha**: lê silêncio
 * para sempre e **nunca pode reabrir**, porque a evidência de que alguém está
 * falando é exatamente o que ele acabou de calar.
 *
 * ★ O cão de guarda da [T084] NÃO resolve isto, e entender por quê importa:
 * com o rAF funcionando, as medições **continuam chegando** — todas zero — e
 * rearmam o cão de guarda para sempre. Ele protege contra a medição que PARA,
 * não contra a medição que MENTE.
 *
 * `track.clone()` devolve uma trilha alimentada pela MESMA fonte, com
 * `enabled` **independente**: o clone fica sempre habilitado e é ele que o
 * medidor escuta; quem é fechado é o original, que vai ao WebRTC. Sem segundo
 * `getUserMedia` — nada de disputar o hardware, mesma razão pela qual o
 * barge-in não abre outro stream.
 *
 * @returns {{paraEscutar: MediaStream, clone: ?MediaStreamTrack}}
 */
const _escutaSemOPortao = (stream, trilhas) => {
  const semClone = { paraEscutar: stream, clone: null }
  if (!trilhas.length || typeof trilhas[0].clone !== 'function') return semClone
  if (typeof globalThis.MediaStream !== 'function') return semClone
  try {
    const clone = trilhas[0].clone()
    return { paraEscutar: new globalThis.MediaStream([clone]), clone: clone }
  } catch (_erro) {
    // Sem clone possível, medir o original é melhor que não medir — e o cão de
    // guarda da [T084] continua sendo a rede embaixo.
    return semClone
  }
}

/**
 * ★ [T100] Um temporizador com nome, no lugar de três `clearTimeout` soltos.
 *
 * O porteiro tem três prazos — fechar depois do silêncio, abrir de vez quando
 * a medição some, e segurar aberto depois de um barge-in — e cada um repetia a
 * mesma dança de guardar o id, limpar antes de rearmar e zerar ao disparar.
 * Três cópias da mesma coisa é onde nasce o quarto prazo que esquece de zerar.
 */
const _criarPrazo = () => {
  let _id = null
  return {
    ativo: () => Boolean(_id),
    armar: (ms, acao) => {
      if (_id) clearTimeout(_id)
      _id = setTimeout(() => {
        _id = null
        acao()
      }, ms)
    },
    cancelar: () => {
      if (!_id) return
      clearTimeout(_id)
      _id = null
    },
  }
}

export const criarPorteiroDoMicrofone = ({ stream, estaMudo, aoMudar }) => {
  let _medidor = null
  let _clone = null
  let _passando = true
  const _fechamento = _criarPrazo()
  const _caoDeGuarda = _criarPrazo()
  // ★ [T100] Enquanto este prazo estiver de pé, o nível não fecha a porta.
  // É PRAZO, e não um instante de `Date.now()`, porque o resto do módulo se
  // mede assim — e porque `Date.now()` não anda com o relógio falso da suíte:
  // a primeira versão desta janela passava nos três casos que a abriam e
  // reprovava no único que exigia que ela FECHASSE, medindo o relógio parado
  // em vez da regra.
  const _janelaDoBargeIn = _criarPrazo()

  const _trilhas = () => {
    if (!stream || typeof stream.getAudioTracks !== 'function') return []
    return stream.getAudioTracks() || []
  }

  const _mudo = () => (typeof estaMudo === 'function' ? Boolean(estaMudo()) : false)

  const _aplicar = (passando) => {
    // ⚠️ Mudo do consultor VENCE: o porteiro nunca reabre o que ele fechou.
    const habilitar = passando && !_mudo()
    _trilhas().forEach((trilha) => {
      trilha.enabled = habilitar
    })
    if (_passando !== passando) {
      _passando = passando
      if (typeof aoMudar === 'function') aoMudar(passando)
    }
  }

  /**
   * ★ [T084] Rearma o cão de guarda a cada medição. Enquanto elas chegam, ele
   * nunca dispara; no instante em que param, ele abre a porta e a mantém
   * aberta — o porteiro se aposenta em vez de emudecer o microfone.
   */
  const _rearmarCaoDeGuarda = () => {
    _caoDeGuarda.armar(MS_SEM_MEDICAO_ABRE_DE_VEZ, () => {
      _fechamento.cancelar()
      _aplicar(true)
    })
  }

  const _aoNivel = (rms) => {
    _rearmarCaoDeGuarda()
    if (rms >= LIMIAR_DO_PORTEIRO_RMS) {
      // Abre JÁ — ver a decisão 1 do cabeçalho.
      _fechamento.cancelar()
      _aplicar(true)
      return
    }
    if (!_passando || _fechamento.ativo()) return
    // ★ [T100] Dentro da janela do barge-in, o nível não tem voz: quem decidiu
    // que é o consultor foi o outro juiz, e ele decidiu com a IA falando por
    // cima — situação em que o RMS do consultor chega ATENUADO ao medidor.
    if (_janelaDoBargeIn.ativo()) return
    _fechamento.armar(MS_DE_SEGURAR_ABERTO, () => _aplicar(false))
  }

  const iniciar = () => {
    if (_medidor) return
    // ⚠️ Falha ABERTA — ver a decisão 3 do cabeçalho. Sem medidor, a trilha
    // fica como está e a voz funciona sem porteiro.
    try {
      const escuta = _escutaSemOPortao(stream, _trilhas())
      _clone = escuta.clone
      _medidor = criarMedidorDeNivel(escuta.paraEscutar)
      _medidor.iniciar(_aoNivel)
      // ⚠️ Armado MESMO SEM medição nenhuma ter chegado: se `iniciar` nunca
      // chamar o callback (rAF pausado desde o começo), é este temporizador que
      // garante a porta aberta. Sem ele, o porteiro dependeria do próprio sinal
      // que está faltando para descobrir que está faltando.
      _rearmarCaoDeGuarda()
    } catch (_erro) {
      _medidor = null
    }
  }

  const parar = () => {
    _fechamento.cancelar()
    _caoDeGuarda.cancelar()
    _janelaDoBargeIn.cancelar()
    if (_medidor) {
      _medidor.parar()
      _medidor = null
    }
    // O clone é nosso: quem o criou o encerra, senão ele segura o microfone
    // aberto depois de a sessão fechar.
    if (_clone) {
      if (typeof _clone.stop === 'function') _clone.stop()
      _clone = null
    }
    // Sai deixando passar: o porteiro nunca é o motivo de um microfone mudo.
    _aplicar(true)
  }

  return {
    iniciar,
    parar,
    /**
     * ★★★ [spec-008 T100] Abre porque o OUTRO juiz decidiu — e o outro juiz só
     * opina com a IA falando.
     *
     * **O defeito que isto fecha, medido.** O barge-in interrompe em 0,04
     * (`LIMIAR_BARGE_IN_RMS`); esta porta abre em 0,15. Na faixa entre os dois o
     * consultor **cala a IA e não é ouvido**: o corte é local, a entrega depende
     * daqui. G4 de 19/09/2026: **calou 5 de 5** (mediana 482 ms) e **retomou 0
     * de 5** — a IA emudece e fica surda, que é pior que não ter interrompido.
     *
     * ⚠️ **A invariante existia, escrita no cabeçalho deste arquivo, e deixou de
     * ser verdade sem nenhum teste reprovar** quando a [T095] subiu o limiar
     * daqui de 0,02 para 0,15 (certo para o G5: 44 disparos → 0). Baixá-lo de
     * volta reabriria o G5. Os dois limiares estão certos para o que cada um
     * decide; o que faltava era a subordinação — a porta não re-julga a decisão
     * do barge-in, ela obedece.
     *
     * O mudo do consultor continua vencendo (é `_aplicar` quem garante).
     */
    abrirAgora: () => {
      _fechamento.cancelar()
      _janelaDoBargeIn.armar(MS_ABERTO_APOS_BARGE_IN, () => {})
      _aplicar(true)
    },
    passando: () => _passando,
  }
}
